import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (target: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

let loader: Promise<void> | null = null;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve();
  if (!loader)
    loader = new Promise((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-bckt-turnstile]');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Turnstile failed to load')), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.defer = true;
      script.dataset.bcktTurnstile = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Turnstile failed to load'));
      document.head.append(script);
    });
  return loader;
}

export function Turnstile({
  siteKey,
  action,
  resetSignal,
  onToken,
}: {
  siteKey: string;
  action: 'login' | 'register' | 'report' | 'verify_email' | 'forgot_password' | 'reset_password' | 'two_factor';
  resetSignal: number;
  onToken: (token: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const widget = useRef<string | undefined>(undefined);
  const callback = useRef(onToken);
  callback.current = onToken;

  useEffect(() => {
    let disposed = false;
    if (!siteKey || !container.current) return;
    void loadTurnstile().then(() => {
      if (disposed || !container.current || !window.turnstile) return;
      widget.current = window.turnstile.render(container.current, {
        sitekey: siteKey,
        action,
        theme: 'dark',
        size: 'flexible',
        appearance: 'interaction-only',
        callback: (token: string) => callback.current(token),
        'expired-callback': () => callback.current(''),
        'error-callback': () => callback.current(''),
      });
    });
    return () => {
      disposed = true;
      if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = undefined;
    };
  }, [siteKey, action]);

  useEffect(() => {
    if (widget.current && window.turnstile) window.turnstile.reset(widget.current);
    callback.current('');
  }, [resetSignal]);

  return <div className="turnstile-field" ref={container} aria-label="Human verification" />;
}
